import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { formatChf, bearbeitungssperre } from '@raporto/shared'
import { ActionButton } from '../ui/ActionButton.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { MailEditor } from '../ui/MailEditor.js'
import { uuidv7 } from '../net/ids.js'
import { OfficeHead } from '../ui/OfficeShell.js'
import { Datumsfeld } from '../ui/Datumsfeld.js'
import { pruefePosition, type Positionsfeld } from '../ui/Positionswerte.js'
import { Vorschlagsfeld } from '../ui/Vorschlagsfeld.js'
import { useKatalogvorschlaege, type Katalogeintrag } from '../ui/Katalogvorschlaege.js'
import { ApiError } from '../net/errors.js'
import {
  api,
  type BueroRechnung,
  type DocumentLine,
  type DocumentDetail,
  type DocumentTotals,
  type Payment,
  type RechnungsFilter,
  type Sprung,
  type ToCatalogAnswer,
  type Versandwege,
  type Zahlungsstand
} from '../net/apiClient.js'
import { PdfAnsicht } from '../ui/PdfAnsicht.js'
import {
  useAuswahl,
  Auswahlkasten,
  AlleAufSeite,
  Auswahlleiste,
  Datumsleiste
} from '../ui/Belegauswahl.js'

/**
 * Rechnungsentwürfe.
 *
 * Was hier passiert, ist die Stelle, an der das Produkt seinen Wert zeigt: die
 * Zeilen kommen aus dem Keller, und niemand tippt sie ab.
 *
 * Drei Dinge sind bewusst so und nicht anders:
 *
 * 1. Geändert wird immer die Belegzeile, nie der Rapport. Der Rapport ist das,
 *    was auf der Baustelle aufgeschrieben und unterschrieben wurde; er bleibt
 *    stehen, auch wenn das Büro umformuliert.
 *
 * 2. Jede Änderung geht sofort zum Server, und die Summe kommt von dort zurück.
 *    Eine im Browser gerechnete Rechnungssumme wäre eine zweite Wahrheit – und
 *    die, die der Kunde bekäme, wäre die ungeprüfte.
 *
 * 3. "In den Katalog übernehmen" ist ein eigener Knopf mit Rückfrage und nie
 *    ein Nebeneffekt des Verrechnens. Ein Katalog, der sich selbst füllt, ist
 *    nach einem halben Jahr unbenutzbar.
 */

type Auswahl = { customerId: string; projectId: string; lineIds: string[] }

/**
 * Zustände in der Sprache des Anwenders.
 *
 * Die Datenbank kennt `nummer_ohne_pdf`; ein Handwerker kennt das nicht und
 * soll es auch nicht lernen müssen. Was er wissen muss, ist, ob er etwas tun
 * kann – deshalb stehen hier Sätze und keine Fachbegriffe.
 */
const ZUSTAND_TEXT: Record<string, string> = {
  entwurf: 'Entwurf',
  wird_abgeschlossen: 'wird abgeschlossen …',
  nummer_ohne_pdf: 'Nummer vergeben, PDF fehlt',
  bereit: 'abgeschlossen',
  wird_gesendet: 'wird gesendet …',
  gesendet: 'gesendet',
  versand_fehlgeschlagen: 'Versand fehlgeschlagen',
  versand_unklar: 'Versand unklar',
  storniert: 'storniert'
}

/**
 * Der Zahlungsstand daneben, nicht darin.
 *
 * Eine gesendete Rechnung kann offen sein, eine nie gesendete bezahlt. Ein
 * einziges Feld für beides zwingt jemanden, aus «gesendet» auf «unbezahlt» zu
 * schliessen – und genau daraus entstehen Mahnungen an Kunden, die längst
 * überwiesen haben.
 */
const ZAHLTEXT: Record<string, string> = {
  offen: 'unbezahlt',
  teilbezahlt: 'teilbezahlt',
  bezahlt: 'bezahlt',
  ueberbezahlt: 'überbezahlt',
  storniert: '–'
}

const datum = (iso: string | null | undefined): string =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('de-CH') : '–'

/* ---------------------------------------------------------------------------
   Die Frist als Weg statt als Datum

   In der Tabelle standen «Datum», «Fällig» und «Zustand» in drei Spalten
   nebeneinander. Wer wissen wollte, wie dringend etwas ist, musste zwei Daten
   voneinander abziehen – bei vierzig Zeilen vierzig Mal.

   Hier läuft ein Balken von der Stellung bis zur Fälligkeit. Steht der Zeiger
   darüber hinaus, ist er rot, läuft über die Marke und sagt um wie viele Tage.
   Die Zahl steht trotzdem daneben: eine Schiene zeigt die Lage, aber wer
   mahnt, braucht den Tag.
   --------------------------------------------------------------------------- */

interface Frist {
  /** 0 bis 100 – wie weit der Balken läuft. */
  lauf: number
  /** Wo die Fälligkeit auf der Schiene sitzt, wenn überschritten. */
  marke: number | null
  art: 'offen' | 'spaet' | 'bezahlt' | 'entwurf'
  links: string
  rechts: string
}

const TAG = 86400000

function frist(d: BueroRechnung): Frist {
  const heute = new Date()
  heute.setHours(0, 0, 0, 0)

  if (!d.number || d.status === 'entwurf') {
    /*
      Auch ein Entwurf nennt sein Rechnungsdatum.

      Hier stand nur «Noch nicht abgeschlossen». Das klang vollständig, war es
      aber nicht: wer eine Rechnung auf den 10. Februar zurückdatiert und sie
      als Entwurf liegen lässt, sah in der Liste dasselbe wie bei einem
      Entwurf von heute. Das Datum ist genau die Angabe, die man an einem
      liegengebliebenen Entwurf noch einmal prüfen will, bevor man ihn
      abschliesst – eine Frist hat er ja noch nicht.
    */
    return {
      lauf: 0,
      marke: null,
      art: 'entwurf',
      links: d.issueDate ? `Entwurf vom ${datum(d.issueDate)}` : 'Entwurf',
      rechts: 'nicht abgeschlossen'
    }
  }
  if (d.status === 'storniert') {
    return {
      lauf: 0,
      marke: null,
      art: 'entwurf',
      links: d.issueDate ? `storniert, gestellt ${datum(d.issueDate)}` : 'Storniert',
      rechts: '—'
    }
  }
  if (d.offenRp <= 0) {
    return {
      lauf: 100,
      marke: null,
      art: 'bezahlt',
      links: `gestellt ${datum(d.issueDate)}`,
      rechts: 'bezahlt'
    }
  }
  if (!d.dueDate) {
    return {
      lauf: 0,
      marke: null,
      art: 'offen',
      links: `gestellt ${datum(d.issueDate)}`,
      rechts: 'ohne Frist'
    }
  }

  const gestellt = Date.parse(`${(d.issueDate ?? d.dueDate).slice(0, 10)}T00:00:00`)
  const faellig = Date.parse(`${d.dueDate.slice(0, 10)}T00:00:00`)
  const zuSpaet = Math.round((heute.getTime() - faellig) / TAG)

  if (zuSpaet > 0) {
    /* Der Überhang wächst mit der Verspätung, aber nicht ins Unendliche –
       bei 28 Tagen ist die Schiene voll, und alles darüber sagt die Zahl. */
    const ueber = Math.min(28, zuSpaet)
    return {
      lauf: 100,
      marke: 100 - ueber,
      art: 'spaet',
      links: `fällig war ${datum(d.dueDate)}`,
      rechts: `${zuSpaet} ${zuSpaet === 1 ? 'Tag' : 'Tage'} über`
    }
  }

  const gesamt = Math.max(1, (faellig - gestellt) / TAG)
  const gelaufen = (heute.getTime() - gestellt) / TAG
  return {
    lauf: Math.max(0, Math.min(100, (gelaufen / gesamt) * 100)),
    marke: null,
    art: 'offen',
    links: `gestellt ${datum(d.issueDate)}`,
    rechts: zuSpaet === 0 ? 'heute fällig' : `in ${-zuSpaet} Tagen fällig`
  }
}

function badgeKlasse(d: { status: string; ueberfaellig: boolean }): string {
  if (d.status === 'storniert' || d.status === 'versand_fehlgeschlagen') return 'o-badge-alert'
  if (d.status === 'nummer_ohne_pdf' || d.status === 'versand_unklar') return 'o-badge-warn'
  if (d.ueberfaellig) return 'o-badge-warn'
  if (d.status === 'gesendet') return 'o-badge-ok'
  return 'o-badge-neutral'
}

export function Documents({
  session,
  auftrag,
  onAuftragErledigt,
  oeffneBeleg,
  onBelegGeoeffnet,
  menuSignal,
  aufBereich,
  sprung,
  onSprungUebernommen,
  aufUebersicht
}: {
  session: SessionUser
  /** Vom Rapportbildschirm übergebene Zeilen, aus denen ein Entwurf wird. */
  auftrag?: Auswahl | null
  onAuftragErledigt?: () => void
  /** Aus der Arbeitsliste angesprungen – direkt diesen Beleg zeigen. */
  oeffneBeleg?: string | null
  onBelegGeoeffnet?: () => void
  /**
   * Zählt jeden Griff ins Menü.
   *
   * Gemessen: Ein Klick auf «Rechnungen» führte nicht zur Rechnungsliste,
   * sondern zurück in den zuletzt geöffneten Beleg. Der Grund ist, dass
   * dieser Bildschirm sich merkt, welcher Beleg offen ist – und der Bereich
   * sich beim Klick gar nicht ändert, weil man schon dort war. Der
   * Menüeintrag tat damit etwas anderes, als er sagt.
   *
   * Eine Zahl statt eines Schalters, weil zwei Klicks hintereinander auf
   * denselben Eintrag zweimal wirken sollen.
   */
  menuSignal?: number
  /** Wechsel in einen anderen Bereich – für den Knopf «Neue Rechnung». */
  aufBereich?: (id: string) => void
  /**
   * Eine Einstellung, die vom Dashboard mitgekommen ist.
   *
   * Sie wird genau einmal übernommen und dann zurückgemeldet – siehe
   * `onSprungUebernommen`. Bliebe sie stehen, setzte sie den Filter bei jedem
   * Neuzeichnen zurück, und ein Klick auf einen anderen Filter hielte nicht.
   */
  sprung?: Sprung | null
  onSprungUebernommen?: () => void
  /** Der Weg zurück auf die Übersicht – nur sichtbar, wenn man von dort kam. */
  aufUebersicht?: () => void
}): JSX.Element {
  const [rows, setRows] = useState<BueroRechnung[]>([])
  /*
    Das PDF wird in der App gezeigt, nicht im Browser daneben.

    Siehe ui/PdfAnsicht.tsx: auf dem Telefon führte der alte Link aus der
    Anwendung heraus, und zurück kam man nur über einen Neustart.
  */
  const [pdfAnsicht, setPdfAnsicht] = useState<{ url: string; titel: string } | null>(null)
  const [zaehler, setZaehler] = useState<Record<string, number>>({})
  const [summe, setSumme] = useState<{ anzahl: number; totalRp: number; offenRp: number } | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  /* Hat die Abfrage ihre Grenze erreicht? Dann zählen auch die Chips oben nur eine Teilmenge. */
  const [kuerzung, setKuerzung] = useState<{ gelesen: number; gesamt: number } | null>(null)
  const [offen, setOffen] = useState<string | null>(null)
  const [zustand, setZustand] = useState<RechnungsFilter>('offen')
  const [suche, setSuche] = useState('')
  /*
    Der Zeitraum. «Rechnungsdatum» ist der Normalfall – danach wird im Büro
    gefragt. Gefiltert wird auf dem Server und nicht hier: Die Abfrage liest
    höchstens 500 Belege, und was erst hier wegfiele, fehlte auch in den
    Zahlen an den Filtern oben.
  */
  const [datum, setDatum] = useState({ art: 'rechnung', von: '', bis: '' })
  /* Sind wir über eine Kachel oder eine Säule des Dashboards hier? */
  const [vomDashboard, setVomDashboard] = useState(false)

  /*
    Die Einstellung vom Dashboard übernehmen – einmal.

    Die Suche wird dabei geleert, und das ist kein Nebeneffekt, sondern der
    Punkt: Wer auf «8 überfällig» tippt, will acht Rechnungen sehen. Stünde
    noch ein alter Suchbegriff im Feld, wären es vielleicht drei, und die Zahl
    auf der Kachel wäre eine Lüge – ohne dass irgendwo stünde, warum.
  */
  useEffect(() => {
    if (!sprung) return
    if (sprung.filter) setZustand(sprung.filter as RechnungsFilter)
    setDatum(sprung.datum ?? { art: 'rechnung', von: '', bis: '' })
    setSuche('')
    setVomDashboard(sprung.herkunft === 'dashboard')
    onSprungUebernommen?.()
  }, [sprung, onSprungUebernommen])

  const laden = useCallback(async (): Promise<void> => {
    const r = await api.officeInvoices(zustand, suche, datum)
    setRows(r.items)
    setZaehler(r.zaehler as unknown as Record<string, number>)
    setSumme(r.summe as { anzahl: number; totalRp: number; offenRp: number })
    setKuerzung(r.gekuerzt ? { gelesen: r.gelesen ?? r.items.length, gesamt: r.gesamt ?? 0 } : null)
    setLoading(false)
  }, [zustand, suche, datum])

  useEffect(() => {
    setLoading(true)
    void laden().catch(() => setLoading(false))
  }, [laden])

  /*
    Ein Griff ins Menü schliesst ein offenes Belegdetail.

    Steht vor dem Sprung-Effekt darunter: Wer aus einer Arbeitsliste einen
    Beleg anspringt, benutzt kein Menü – die beiden können sich nicht in die
    Quere kommen. Beim ersten Zeichnen ist nichts offen, also schliesst der
    Effekt dann nichts.
  */
  useEffect(() => {
    if (menuSignal === undefined) return
    setOffen(null)
  }, [menuSignal])

  // Ein Sprung aus der Arbeitsliste öffnet direkt den Beleg. Ohne das landet
  // man in einer Liste und muss den Eintrag suchen, den man gerade angetippt
  // hat – der häufigste Grund, warum eine Arbeitsliste nicht benutzt wird.
  useEffect(() => {
    if (!oeffneBeleg) return
    setOffen(oeffneBeleg)
    onBelegGeoeffnet?.()
  }, [oeffneBeleg, onBelegGeoeffnet])

  // Ein Auftrag vom Rapportbildschirm legt den Entwurf an und öffnet ihn.
  useEffect(() => {
    if (!auftrag) return
    void (async () => {
      try {
        const r = await api.createDocument(
          {
            customerId: auftrag.customerId,
            projectId: auftrag.projectId,
            rapportLineIds: auftrag.lineIds
          },
          uuidv7()
        )
        setOffen(r.document.id)
        notify.ok(`Entwurf mit ${r.lineCount} Positionen angelegt.`)
      } catch (err) {
        notifyError(err)
      } finally {
        onAuftragErledigt?.()
        void laden()
      }
    })()
  }, [auftrag, onAuftragErledigt, laden])

  /*
    ══════════════════════════════════════════════════════════════════════════
    Diese drei Haken stehen VOR dem Absprung in die Belegansicht – und zwar
    zwingend
    ══════════════════════════════════════════════════════════════════════════

    Sie standen darunter, und das war ein Absturz: React verlangt, dass eine
    Komponente bei jedem Durchlauf dieselbe Zahl Haken aufruft. In der Liste
    liefen drei Haken mehr als nach `if (offen) return` – und in dem
    Augenblick, in dem jemand eine Rechnung öffnete, brach die Oberfläche mit
    «Minified React error #300» ab. Weisse Seite, kein Hinweis, nichts mehr
    anklickbar; der Weg zurück führte nur über das Neuladen.

    Gefunden bei der Sichtprüfung dieser Runde, am gebauten Stand. Der Fehler
    stammt nicht aus dem Hotfix, sondern kam mit der Mehrfachauswahl der
    Belegliste in diese Datei – er wäre mit dem Hotfix mitgefahren, weil die
    Übergabe die ganze Datei nennt.

    Die Haken kosten in der Belegansicht nichts: `rows` ist dort dieselbe
    Liste, `useMemo` rechnet nicht neu, und ausgewertet wird das Ergebnis erst
    in der Liste darunter.
  */
  const sichtbar = useMemo(() => rows.map((d) => d.id), [rows])
  const auswahl = useAuswahl(sichtbar)
  /*
    Was die Leiste über die Auswahl wissen muss: Nummer, Zustand, PDF. Mehr
    nicht – danach richtet sich, welche Sammelaktion überhaupt passt.

    Die Auswahl hängt an den sichtbaren Zeilen: Wer filtert oder sucht,
    verliert die Haken an Belegen, die nicht mehr dastehen. Eine unsichtbare
    Auswahl ist eine Falle – siehe ui/Belegauswahl.tsx.
  */
  const gewaehlteBelege = useMemo(
    () =>
      rows
        .filter((d) => auswahl.istGewaehlt(d.id))
        .map((d) => ({
          id: d.id,
          nummer: d.number,
          status: d.status,
          hasPdf: d.hasPdf,
          bezeichnung: d.number ?? `Entwurf · ${d.customerName}`,
          offenRp: d.offenRp
        })),
    [rows, auswahl]
  )

  if (offen) {
    return (
      <BelegDetail
        session={session}
        id={offen}
        onBack={() => {
          setOffen(null)
          void laden()
        }}
      />
    )
  }

  const FILTER: Array<{ id: RechnungsFilter; label: string }> = [
    { id: 'offen', label: 'Offen' },
    { id: 'ueberfaellig', label: 'Überfällig' },
    { id: 'entwurf', label: 'Entwürfe' },
    { id: 'bezahlt', label: 'Bezahlt' },
    { id: 'gesendet', label: 'Gesendet' },
    { id: 'storniert', label: 'Storniert' },
    { id: 'alle', label: 'Alle' }
  ]

  return (
    <>
      {pdfAnsicht && (
        <PdfAnsicht
          url={pdfAnsicht.url}
          titel={pdfAnsicht.titel}
          aufZu={() => setPdfAnsicht(null)}
        />
      )}
      <OfficeHead
        titel="Rechnungen"
        unter={summe ? `CHF ${formatChf(summe.offenRp)} stehen offen` : 'Wird geladen …'}
      >
        {/*
          Der Weg zurück – nur, wenn man von dort kam.

          Ein «Zurück zur Übersicht», das immer dasteht, ist ein zweiter Weg
          in die Seitenleiste und damit Zierde. Hier steht er genau dann, wenn
          jemand über eine Kachel hereingekommen ist: Dann ist die Übersicht
          der Ort, an dem er war, und am Telefon liegt die Seitenleiste hinter
          dem Menüknopf.
        */}
        {vomDashboard && (
          <button
            type="button"
            className="o-btn o-btn-ghost"
            id="btn-zurueck-uebersicht"
            onClick={() => aufUebersicht?.()}
          >
            ← Übersicht
          </button>
        )}
        <button
          className="o-btn o-btn-primary"
          id="btn-neue-rechnung"
          onClick={() => aufBereich?.('express')}
        >
          Neue Rechnung
        </button>
      </OfficeHead>

      <div className="o-card">
        <div className="o-pad">
          <div className="o-suchzeile">
            <div className="o-filter" id="rechnungsfilter">
              {FILTER.map((f) => (
                <button
                  key={f.id}
                  id={`docfilter-${f.id}`}
                  className={f.id === zustand ? 'o-chip aktiv' : 'o-chip'}
                  aria-pressed={f.id === zustand}
                  onClick={() => setZustand(f.id)}
                >
                  {f.label}
                  <span className="o-chip-zahl">{zaehler[f.id] ?? 0}</span>
                </button>
              ))}
            </div>
            <div className="o-field o-feldsuche">
              <label htmlFor="rechnung-suche">Suche</label>
              <input
                id="rechnung-suche"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Rechnungsnummer, Kunde oder Projekt"
                autoComplete="off"
              />
            </div>
          </div>
          <Datumsleiste
            kennung="rechnung"
            arten={[
              { id: 'rechnung', label: 'Rechnungsdatum' },
              { id: 'faellig', label: 'Fälligkeit' },
              { id: 'erstellt', label: 'Erstellt' }
            ]}
            wert={datum}
            aufWert={setDatum}
          />
        </div>
      </div>

      <div className="o-card" id="rechnungsliste">
        <div className="o-card-head">
          <h3>
            {FILTER.find((f) => f.id === zustand)?.label}{' '}
            <span className="o-badge o-badge-accent">{rows.length}</span>
          </h3>
          {kuerzung ? (
            <span className="o-soft" id="rechnungen-kuerzung">
              Gelesen werden die {kuerzung.gelesen} neuesten von {kuerzung.gesamt} Belegen – auch
              die Zahlen an den Filtern oben. Für ältere bitte suchen.
            </span>
          ) : (
            <span className="o-soft">
              Zahlungsstand aus den erfassten Zahlungen gerechnet, nicht gespeichert.
            </span>
          )}
        </div>

        {loading ? (
          <div className="o-leer">Wird geladen …</div>
        ) : rows.length === 0 ? (
          <div className="o-leer" id="belege-leer">
            {suche.trim() ? (
              <>
                <strong>Kein Treffer.</strong>
                Gesucht wird über Rechnungsnummer, Kunde und Projekt.
              </>
            ) : zustand === 'offen' ? (
              <>
                <strong>Keine offene Forderung.</strong>
                Alles, was hinausgegangen ist, ist bezahlt.
              </>
            ) : zustand === 'entwurf' ? (
              <>
                <strong>Kein Entwurf.</strong>
                Entwürfe entstehen aus Rapporten oder über die Express-Rechnung.
              </>
            ) : (
              <>
                <strong>Nichts in dieser Auswahl.</strong>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="o-wahlzeile">
              <AlleAufSeite stand={auswahl} anzahl={rows.length} />
            </div>
            <div className="o-belege" id="belegliste">
              {rows.map((d) => {
                const f = frist(d)
                return (
                  <article
                    key={d.id}
                    id={`beleg-${d.id}`}
                    className={d.status === 'storniert' ? 'o-beleg inaktiv' : 'o-beleg'}
                    onClick={() => setOffen(d.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setOffen(d.id)
                      }
                    }}
                  >
                    <Auswahlkasten
                      id={d.id}
                      stand={auswahl}
                      titel={`${d.number ?? 'Entwurf'} auswählen`}
                    />
                    <span className={`o-beleg-punkt ${f.art}`} aria-hidden="true" />

                    <div className="o-beleg-wer">
                      <b>{d.customerName}</b>
                      <span>
                        {d.number ?? 'Entwurf'}
                        {d.projectName && <i> · {d.projectName}</i>}
                      </span>
                    </div>

                    {/*
                    Die Frist als Schiene, nicht als Datum.

                    In der Tabelle standen «Datum», «Fällig» und «Zustand» in
                    drei Spalten nebeneinander, und wer wissen wollte, wie
                    dringend etwas ist, musste zwei Daten voneinander abziehen.
                    Hier läuft der Balken von der Stellung bis zur Fälligkeit;
                    ragt er darüber hinaus, ist er rot und sagt um wie viel.
                  */}
                    <div className="o-frist">
                      <div className="o-frist-schiene">
                        <div className={`o-frist-lauf ${f.art}`} style={{ width: `${f.lauf}%` }} />
                        {f.marke !== null && (
                          <div className="o-frist-marke" style={{ left: `${f.marke}%` }} />
                        )}
                      </div>
                      <div className="o-frist-text">
                        <span>{f.links}</span>
                        <b className={f.art} id={`bzustand-${d.id}`}>
                          {f.rechts}
                        </b>
                      </div>
                    </div>

                    <div className="o-beleg-geld">
                      <b>{formatChf(d.totalRp)}</b>
                      {/* Ein bezahlter Beleg zeigt keinen zweiten Betrag – eine
                        0.00 in einer Geldspalte liest sich wie ein Betrag. */}
                      {d.offenRp > 0 && d.offenRp < d.totalRp ? (
                        <span className="offen">noch {formatChf(d.offenRp)} offen</span>
                      ) : (
                        <span>{d.nextActionLabel ?? ZAHLTEXT[d.zahlstatus] ?? ''}</span>
                      )}
                    </div>

                    <div className="o-beleg-griffe">
                      {d.hasPdf && (
                        <button
                          type="button"
                          className="o-griff"
                          id={`bpdf-${d.id}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            setPdfAnsicht({
                              url: `/documents/${d.id}/pdf`,
                              titel: d.number ?? 'Beleg'
                            })
                          }}
                        >
                          PDF
                        </button>
                      )}
                      <span className="o-griff o-griff-still">Öffnen</span>
                    </div>
                  </article>
                )
              })}
            </div>
          </>
        )}
      </div>

      <Auswahlleiste
        stand={auswahl}
        belege={gewaehlteBelege}
        was="Rechnung"
        aufGetan={() => void laden()}
      />
    </>
  )
}

// ---------------------------------------------------------------------------

/**
 * Ein Beleg im Detail – Rechnung wie Offerte.
 *
 * Exportiert, weil die Offertenliste denselben Bildschirm braucht. Zwei
 * Fassungen hiessen: jede Verbesserung an der Positionsbearbeitung zweimal
 * machen, und beim zweiten Mal vergessen.
 */
export function BelegDetail({
  session,
  id,
  onBack
}: {
  session: SessionUser
  id: string
  onBack: () => void
}): JSX.Element {
  const [daten, setDaten] = useState<{
    document: DocumentDetail
    lines: DocumentLine[]
    totals: DocumentTotals
  } | null>(null)
  const [bearbeitet, setBearbeitet] = useState<string | null>(null)
  const [pdfAnsicht, setPdfAnsicht] = useState<{ url: string; titel: string } | null>(null)
  const [frage, setFrage] = useState<{ lineId: string; antwort: ToCatalogAnswer } | null>(null)
  const [stornoGrund, setStornoGrund] = useState('')
  const [stornoOffen, setStornoOffen] = useState(false)

  const laden = useCallback(async (): Promise<void> => {
    const r = await api.getDocument(id)
    setDaten(r as unknown as typeof daten)
  }, [id])

  useEffect(() => {
    void laden().catch(() => setDaten(null))
  }, [laden])

  if (!daten) {
    return (
      <div className="card">
        <p className="muted">Wird geladen …</p>
        <ActionButton
          variant="quiet"
          labels={{ idle: 'Zurück', busy: '…' }}
          onAction={async () => onBack()}
        />
      </div>
    )
  }

  const { document: beleg, lines, totals } = daten
  const entwurf = beleg.status === 'entwurf'
  /*
    ══════════════════════════════════════════════════════════════════════════
    Änderbar heisst: noch nicht draussen – nicht «noch Entwurf»
    ══════════════════════════════════════════════════════════════════════════

    Aus Produktion gemeldet, nach PR #15: Eine abgeschlossene Rechnung ohne
    Versandvermerk zeigte «Dieser Beleg ist abgeschlossen und lässt sich nicht
    mehr ändern.», und es gab keinen Knopf. Der Server hätte die Änderung
    längst angenommen – `ladeBearbeitbar` prüft seit dem Hotfix den Versand
    und nicht den Status. Die Maske fragte weiter nach `entwurf`.

    Das war kein Schönheitsfehler, sondern genau der Fall, den der Hotfix
    beheben sollte: Wer sich in der Menge vertippt hat, musste stornieren und
    von vorne beginnen – und verbrannte dabei jedes Mal eine Nummer.

    Deshalb wird hier nicht mehr geraten, sondern gefragt, und zwar mit
    derselben Funktion, die der Server benutzt. Eine zweite Umsetzung
    derselben Regel in der Oberfläche wäre genau der Weg, auf dem die beiden
    Seiten wieder auseinanderlaufen.

    `entwurf` bleibt daneben bestehen – für das, was wirklich nur einen
    Entwurf betrifft: abschliessen und verwerfen. Beides hat mit der
    Bearbeitbarkeit nichts zu tun.
  */
  const sperre = bearbeitungssperre({
    status: beleg.status,
    gesendetAnKundeAm: beleg.gesendetAnKundeAm,
    gesendetAnFirmaAm: beleg.gesendetAnFirmaAm,
    gesendetAnBenutzerAm: beleg.gesendetAnBenutzerAm,
    versendetVermerktAm: beleg.versendetVermerktAm
  })
  const aenderbar = sperre === null
  /*
    0.00 sperrt nichts mehr.

    Der Knopf war an «keine Position ohne Preis» gebunden, und damit liess
    sich keine Rechnung mehr abschliessen, auf der etwas gratis stand –
    Garantiearbeit, Anfahrt, eine Zeile «inklusive». Gemeldet aus Produktion,
    und zu Recht.

    Gesperrt wird jetzt nur noch, was der Server auch ablehnt: gar keine
    Position, ein negativer Preis, eine Menge von null oder weniger. Alles
    andere ist die Entscheidung des Betriebs und nicht die der Oberfläche.

    `ohnePreis` bleibt – aber als Hinweis, nicht als Sperre: Freitext vom
    Monteur kommt ohne Preis an, und darauf hinzuweisen ist weiter richtig.
  */
  const ohnePreis = lines.filter((l) => l.unitPriceRp === 0)
  const negativ = lines.filter((l) => l.unitPriceRp < 0 || Number(l.quantity) <= 0)
  const abschliessbar = entwurf && lines.length > 0 && negativ.length === 0
  const hatNummer = Boolean(beleg.number)
  const pdfFehlt = beleg.status === 'nummer_ohne_pdf'
  /*
    Offerte oder Rechnung – derselbe Bildschirm, drei Unterschiede.

    Eine Offerte hat kein Geld: keine Zahlungen, kein Zahlteil, kein Versand
    über den Rechnungsweg. Und sie hat ein Ablaufdatum, das eine Rechnung
    nicht kennt. Der Rest – Positionen, Summe, Abschliessen, Stornieren – ist
    identisch, und genau deshalb steht er nur einmal da.
  */
  const istOfferte = beleg.type === 'offerte'
  const wort = istOfferte ? 'Offerte' : 'Rechnung'
  /*
    Zwei Fragen, die bisher eine waren.

    `versandbereit` hiess «hat eine Nummer, hat ein PDF, und ist keine
    Offerte» – und wurde sowohl für den Versandknopf als auch für den Link
    «PDF ansehen» benutzt. Der Versandteil stimmt: eine Offerte geht nicht über
    den Rechnungsweg hinaus. Der PDF-Teil stimmt nicht: eine abgeschlossene
    Offerte HAT ein PDF, und im Offertendetail war es trotzdem nie zu öffnen.
    Der einzige Weg dorthin war der kleine Link in der Offertenliste.
  */
  const pdfDa = hatNummer && !pdfFehlt
  /*
    Eine stornierte Rechnung wird nicht verschickt.

    Bis hierher hiess versandbereit «hat Nummer, hat PDF, ist keine Offerte» –
    und stand damit auch unter einer stornierten Rechnung. Das Feld «Versand»
    mit dem Knopf «Per E-Mail senden» fragt dort nach etwas, das niemand tun
    will: eine Forderung zuschicken, die der Betrieb gerade zurückgenommen hat.
    Wer sie doch versendet, hat einen Kunden, der eine stornierte Rechnung
    bezahlt.

    Das PDF bleibt abrufbar – für die Ablage und für den Treuhänder. Nur der
    Weg nach draussen ist zu.
  */
  const storniert = beleg.status === 'storniert'
  const versandbereit = pdfDa && !istOfferte && !storniert

  return (
    <>
      {pdfAnsicht && (
        <PdfAnsicht
          url={pdfAnsicht.url}
          titel={pdfAnsicht.titel}
          aufZu={() => setPdfAnsicht(null)}
        />
      )}
      <div className="card slim">
        <button type="button" id="btn-beleg-zurueck" className="backlink" onClick={onBack}>
          ← {istOfferte ? 'Offerten' : 'Rechnungen'}
        </button>
      </div>

      <div className="card">
        <div className="listhead">
          <h2>{beleg.customerName}</h2>
          <span className="muted" id="beleg-zustand">
            {ZUSTAND_TEXT[beleg.status] ?? beleg.status}
          </span>
        </div>
        <p className="muted small" id="beleg-art">
          {wort}
          {istOfferte && beleg.validUntil ? ` · gültig bis ${datum(beleg.validUntil)}` : ''}
          {istOfferte && beleg.quoteOutcome ? ` · ${beleg.quoteOutcome}` : ''}
        </p>
        {/*
          Die Baustelle, und ob sie eine ist.

          `projectName` kommt vom Server als COALESCE aus dem Namen der
          verknüpften Baustelle und dem Freitext auf dem Beleg. Beides sah
          hier gleich aus – und damit war im Detail nicht erkennbar, ob der
          Beleg wirklich an einer Baustelle hängt oder ob dort nur jemand
          etwas hingeschrieben hat. Genau das war die Frage aus dem Büro.
        */}
        {beleg.projectName && (
          <p className="muted small" id="beleg-projekt">
            Projekt {beleg.projectName}
            {!beleg.projectId && <i> · nur als Text auf dem Beleg</i>}
          </p>
        )}
        {/*
          Leistungszeitraum und Herkunft gehören hierher, nicht erst in die
          Nummernkarte.

          Die Nummernkarte erscheint erst nach dem Abschliessen. Genau
          dazwischen liegt aber der Moment, in dem jemand nachsieht, ob die aus
          einer Offerte erzeugte Rechnung die richtige Leistungszeit hat – im
          Entwurf, solange sich noch etwas ändern lässt. Stand die Angabe erst
          auf dem fertigen PDF, war die Prüfung zu spät.
        */}
        {(beleg.serviceFrom || beleg.serviceTo) && (
          <p className="muted small" id="beleg-leistung">
            Leistung{' '}
            {beleg.serviceFrom && beleg.serviceTo && beleg.serviceFrom !== beleg.serviceTo
              ? `${datum(beleg.serviceFrom)} – ${datum(beleg.serviceTo)}`
              : datum(beleg.serviceFrom ?? beleg.serviceTo)}
          </p>
        )}
        {beleg.sourceQuoteNumber && (
          <p className="muted small" id="beleg-herkunft">
            Aus Offerte <span className="mono">{beleg.sourceQuoteNumber}</span>
          </p>
        )}
        {/*
          Der Satz nennt jetzt den WIRKLICHEN Grund.

          Vorher stand hier «abgeschlossen und lässt sich nicht mehr ändern»
          an jedem Beleg mit Nummer – auch an denen, die der Server sehr wohl
          noch ändern lässt. `bearbeitungssperre` liefert den Grund und den
          Weg, der stattdessen offensteht; storniert, versendet und «Versand
          unklar» sagen Verschiedenes.
        */}
        {sperre && (
          <p className="muted small" id="beleg-gesperrt">
            {beleg.status === 'storniert'
              ? 'Dieser Beleg ist storniert. Die Rapportzeilen sind wieder frei und lassen sich neu verrechnen.'
              : sperre.text}
          </p>
        )}
        {/*
          Und das Gegenstück: Wer eine nummerierte Rechnung ändert, soll
          wissen, dass das Blatt neu gedruckt wird. Still zu ändern wäre
          genauso falsch wie zu sperren.
        */}
        {aenderbar && hatNummer && (
          <p className="muted small" id="beleg-aenderbar">
            Diese {wort} ist noch nicht versendet und lässt sich ändern. Die Nummer bleibt; das
            PDF wird nach der Änderung neu erzeugt.
          </p>
        )}
        {beleg.notes && <p className="small">{beleg.notes}</p>}
      </div>

      {aenderbar && ohnePreis.length > 0 && (
        <div className="card" id="hinweis-ohne-preis">
          <h2>Positionen zu 0.00</h2>
          <p className="muted small">
            {ohnePreis.length === 1
              ? 'Eine Position steht'
              : `${ohnePreis.length} Positionen stehen`}{' '}
            auf 0.00 CHF. Wenn das so gemeint ist – Garantie, Anfahrt, «inklusive» –, lässt sich der
            Beleg so abschliessen. Freitext vom Monteur kommt ohne Preis an; dort fehlt er meistens
            wirklich.
          </p>
        </div>
      )}

      <div className="card">
        <h2>Positionen</h2>
        <ul className="records">
          {lines.map((l) => (
            <li key={l.id}>
              {bearbeitet === l.id && aenderbar ? (
                <ZeileBearbeiten
                  documentId={id}
                  line={l}
                  basisVersion={beleg.version}
                  onFertig={async () => {
                    setBearbeitet(null)
                    await laden()
                  }}
                  onAbbruch={() => setBearbeitet(null)}
                  onKonflikt={laden}
                />
              ) : (
                <div className="line-row">
                  <span className="rec-name">
                    {l.description}
                    {l.fromFreetext && <span className="pill small">Freitext</span>}
                    {l.isExtra && <span className="pill small">Regie</span>}
                    {l.sourceRapportLineId && <span className="pill small">aus Rapport</span>}
                    {l.unitPriceRp === 0 && <span className="pill small warn">Preis offen</span>}
                  </span>
                  <span className="rec-sub">
                    {Number(l.quantity)} {l.unit} × CHF {formatChf(l.unitPriceRp)}
                    {Number(l.discountPercent) > 0
                      ? ` − ${Number(l.discountPercent)} %`
                      : ''} = <strong>CHF {formatChf(l.lineTotalRp)}</strong>
                  </span>
                  {aenderbar && (
                    <span className="rowactions">
                      <button
                        type="button"
                        id={`edit-${l.id}`}
                        className="line-remove"
                        onClick={() => setBearbeitet(l.id)}
                      >
                        Ändern
                      </button>
                      {l.fromFreetext && l.unitPriceRp > 0 && (
                        <button
                          type="button"
                          id={`tocat-${l.id}`}
                          className="line-remove"
                          onClick={async () => {
                            try {
                              const antwort = await api.lineToCatalog(id, l.id, {}, uuidv7())
                              setFrage({ lineId: l.id, antwort })
                            } catch (err) {
                              notifyError(err)
                            }
                          }}
                        >
                          In Katalog
                        </button>
                      )}
                      <button
                        type="button"
                        id={`del-${l.id}`}
                        className="line-remove"
                        onClick={async () => {
                          try {
                            const r = await api.removeDocumentLine(id, l.id, uuidv7(), beleg.version)
                            await laden()
                            notify.info(
                              r.freigegeben
                                ? 'Position entfernt. Die Rapportzeile ist wieder frei.'
                                : 'Position entfernt.'
                            )
                          } catch (err) {
                            if (istVersionskonflikt(err)) {
                              notify.info(
                                'Dieser Beleg wurde inzwischen an anderer Stelle geändert. ' +
                                  'Die Ansicht ist neu geladen – bitte prüfen, ob die Position noch entfernt werden soll.'
                              )
                              await laden()
                              return
                            }
                            notifyError(err)
                          }
                        }}
                      >
                        Entfernen
                      </button>
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>

        {aenderbar && <FreiePosition documentId={id} basisVersion={beleg.version} onFertig={laden} onKonflikt={laden} />}
      </div>

      {frage && (
        <div className="card problemcard">
          <h2>In den Katalog übernehmen</h2>
          <p>{frage.antwort.message}</p>
          {frage.antwort.similar.length > 0 && (
            <ul className="records">
              {frage.antwort.similar.map((s) => (
                <li key={s.id}>
                  <div className="line-row">
                    <span className="rec-name">{s.name}</span>
                    <span className="rec-sub">
                      {s.code ? `${s.code} · ` : ''}CHF {formatChf(s.unitPriceRp)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="muted small">
            Die Rapportzeile bleibt Freitext. Rückwirkend ändert sich nichts.
          </p>
          <div className="rowactions">
            <ActionButton
              id="btn-tocat-ja"
              labels={{ idle: 'Trotzdem anlegen', busy: 'Legt an …', success: 'Im Katalog' }}
              onAction={async () => {
                await api.lineToCatalog(id, frage.lineId, { confirm: true }, uuidv7())
                setFrage(null)
                await laden()
              }}
              onError={(err) => notifyError(err)}
            />
            <ActionButton
              variant="quiet"
              labels={{ idle: 'Abbrechen', busy: '…' }}
              onAction={async () => setFrage(null)}
            />
          </div>
        </div>
      )}

      <div className="card">
        <h2>Summe</h2>
        <dl className="counts">
          {/*
            Der Gesamtrabatt wird hergeleitet, nicht bloss verrechnet.

            Vorher stand hier nur «Netto», und zwar das Netto NACH Rabatt –
            eine Zahl, die nicht zur Summe der Zeilen darüber passte. Wer
            den Beleg prüft, sieht jetzt dieselben drei Schritte wie auf dem
            Papier.
          */}
          {(totals.rabattRp ?? 0) > 0 && (
            <div style={{ display: 'contents' }}>
              <dt>Zwischentotal vor Rabatt</dt>
              <dd>CHF {formatChf(totals.zwischentotalRp ?? totals.subtotalRp)}</dd>
              <dt>Rabatt total {Number(totals.rabattProzent ?? 0)} %</dt>
              <dd id="summe-rabatt">− CHF {formatChf(totals.rabattRp ?? 0)}</dd>
            </div>
          )}
          <dt>Netto</dt>
          <dd id="summe-netto">CHF {formatChf(totals.subtotalRp)}</dd>
          {/*
            Ein Satz von 0 bedeutet: der Betrieb ist nicht
            mehrwertsteuerpflichtig. Dann gehört keine Zeile «MWST 0 %» hin,
            weder hier noch auf dem Beleg.
          */}
          {(totals.vatBySatz ?? [])
            .filter((v) => v.rate > 0)
            .map((v) => (
              <div key={v.rate} style={{ display: 'contents' }}>
                <dt>MWST {v.rate} %</dt>
                <dd>CHF {formatChf(v.vatRp)}</dd>
              </div>
            ))}
          {/*
            Die Rundungszeile – nur wenn wirklich gerundet wurde.

            Sie fehlte hier, und das war die Zahl, die niemand nachrechnen
            konnte: Netto 6'375.00 plus MWST 516.38 steht da, und darunter ein
            Total von 6'891.40. Die zwei Rappen kommen aus der
            Fünf-Rappen-Rundung, und auf dem PDF stehen sie seit dem Hotfix
            ausgewiesen. Hier nicht – damit sah die richtige Summe falsch aus,
            und wer nachrechnete, hielt den Beleg für fehlerhaft.

            Dieselbe Zeile, derselbe Wortlaut wie im PDF und im
            Express-Bildschirm. Steht `rundungRp` auf 0, steht hier nichts.
          */}
          {(totals.rundungRp ?? 0) !== 0 && (
            <div style={{ display: 'contents' }}>
              <dt>Rundung auf 5 Rappen</dt>
              <dd id="summe-rundung">
                {(totals.rundungRp ?? 0) < 0 ? '−' : '+'} CHF{' '}
                {formatChf(Math.abs(totals.rundungRp ?? 0))}
              </dd>
            </div>
          )}
          <dt>
            <strong>Total</strong>
          </dt>
          <dd id="summe-total">
            <strong>CHF {formatChf(totals.totalRp)}</strong>
          </dd>
        </dl>
      </div>

      {hatNummer && (
        <div className={`card ${pdfFehlt ? 'problemcard' : ''}`}>
          <h2>{pdfFehlt ? 'Nummer vergeben, PDF fehlt' : wort}</h2>
          <dl>
            <dt>{istOfferte ? 'Offertnummer' : 'Rechnungsnummer'}</dt>
            <dd className="mono" id="beleg-nummer">
              {beleg.number}
            </dd>
            {/*
              Die Herkunft dazuschreiben, wenn es eine gibt.

              Offerten und Rechnungen laufen in getrennten Nummernkreisen, und
              beide beginnen bei 1. OFF-…-1 und RE-…-1 stehen also am selben
              Tag nebeneinander, und in der Datenbank ist das eindeutig. Auf
              dem Bildschirm war es das nicht: wer eine Rechnung aus einer
              Offerte gemacht hatte, sah zwei Belege mit derselben laufenden
              Zahl und keinen Hinweis, dass der eine aus dem anderen stammt.
            */}
            {beleg.sourceQuoteNumber && (
              <>
                <dt>Aus Offerte</dt>
                <dd className="mono" id="beleg-aus-offerte">
                  {beleg.sourceQuoteNumber}
                </dd>
              </>
            )}
          </dl>
          {pdfFehlt ? (
            <>
              <p className="muted small">
                Die Nummer ist vergeben und bleibt es – eine Rechnungsnummer wird nie
                zurückgenommen, sonst entsteht eine Lücke, die niemand erklären kann. Es fehlt nur
                das PDF.
                {daten.document.notes ? '' : ''}
              </p>
              {lastError(beleg) && <p className="small">{lastError(beleg)}</p>}
            </>
          ) : (
            <p className="muted small">Abgeschlossen. Positionen lassen sich nicht mehr ändern.</p>
          )}

          <div className="rowactions">
            {pdfDa && (
              <button
                type="button"
                className="linkbutton"
                id="btn-pdf-ansehen"
                onClick={() =>
                  setPdfAnsicht({ url: `/documents/${id}/pdf`, titel: beleg.number ?? wort })
                }
              >
                PDF ansehen
              </button>
            )}
            {session.role === 'superuser' && (
              <ActionButton
                id="btn-pdf-neu"
                variant={pdfFehlt ? 'primary' : 'quiet'}
                actionClass="B"
                labels={{
                  idle: 'PDF erneut erzeugen',
                  busy: 'Erzeugt …',
                  success: 'PDF erzeugt'
                }}
                onAction={async () => {
                  const r = await api.regeneratePdf(id, uuidv7())
                  await laden()
                  if (!r.pdfReady) {
                    notify.error(r.error ?? 'Das PDF konnte nicht erzeugt werden.')
                  }
                }}
                onError={(err) => notifyError(err)}
              />
            )}
          </div>
        </div>
      )}

      {/* Eine Offerte ist ein Angebot, keine Forderung – auf ihr gibt es
          nichts zu bezahlen und deshalb auch nichts zu erfassen. */}
      {hatNummer && !istOfferte && <Zahlungen documentId={id} beleg={beleg} rolle={session.role} />}

      {versandbereit && session.role === 'superuser' && (
        <Versand documentId={id} beleg={beleg} onGesendet={laden} />
      )}

      {entwurf && session.role === 'superuser' && (
        <div className="card">
          <h2>Abschliessen</h2>
          <p className="muted small">
            {istOfferte
              ? 'Beim Abschliessen vergibt der Server die Offertnummer, setzt die Gültigkeit und druckt das PDF. Danach ist die Offerte nicht mehr änderbar. Kein Zahlteil – eine Offerte ist kein Zahlungsbeleg.'
              : 'Beim Abschliessen vergibt der Server die Rechnungsnummer, erzeugt die Referenz und druckt das PDF mit QR-Zahlteil. Danach ist die Rechnung nicht mehr änderbar.'}
          </p>
          {!abschliessbar && (
            <p className="small" id="abschluss-gesperrt">
              {lines.length === 0
                ? 'Ohne Positionen gibt es nichts abzuschliessen.'
                : `${negativ.length === 1 ? 'Eine Position hat' : `${negativ.length} Positionen haben`} keine Menge oder einen negativen Preis. Die Menge muss grösser als null sein; der Preis darf 0.00 sein.`}
            </p>
          )}
          <ActionButton
            id="btn-finalisieren"
            actionClass="B"
            disabled={!abschliessbar}
            labels={{
              idle: `${wort} abschliessen`,
              busy: 'Schliesst ab …',
              success: 'Abgeschlossen'
            }}
            escapeLabel="Nachschauen, ob es geklappt hat"
            onAction={async () => {
              const r = await api.finalizeDocument(id, uuidv7())
              await laden()
              if (r.pdfReady) notify.ok(`${wort} ${r.number} ist bereit.`)
              else {
                notify.error(
                  `${wort} ${r.number} hat eine Nummer, aber noch kein PDF. ${r.error ?? ''}`
                )
              }
            }}
            onError={(err) => notifyError(err)}
          />
        </div>
      )}

      {entwurf && session.role === 'superuser' && (
        <div className="card">
          <h2>Entwurf</h2>
          <p className="muted small">
            Verwerfen löscht den Entwurf; alle Rapportzeilen werden wieder frei und können neu
            verrechnet werden. Eine Nummer ist noch nicht vergeben, es bleibt also keine Lücke.
          </p>

          <ActionButton
            id="btn-entwurf-verwerfen"
            variant="secondary"
            actionClass="B"
            labels={{ idle: 'Entwurf verwerfen', busy: 'Verwirft …', success: 'Verworfen' }}
            onAction={async () => {
              const r = await api.discardDocument(id, uuidv7())
              notify.info(r.message)
              onBack()
            }}
            onError={(err) => notifyError(err)}
          />
        </div>
      )}

      {/*
        Stornieren gehört zum abgeschlossenen Beleg, nicht zum Entwurf.

        Bis hierher stand der Storno-Knopf im Kasten «Entwurf» und war damit an
        `entwurf` gebunden – also genau an den einen Zustand, in dem man NICHT
        storniert, sondern verwirft. Wer eine abgeschlossene Rechnung
        zurücknehmen wollte, fand in der Oberfläche keinen Weg dazu, obwohl der
        Server ihn die ganze Zeit angeboten hat. Umgekehrt liess sich ein
        Entwurf stornieren und wurde so zu einem Beleg ohne Nummer, den keine
        Liste je wieder zeigt.
      */}
      {hatNummer && beleg.status !== 'storniert' && session.role === 'superuser' && (
        <div className="card">
          <h2>Stornieren</h2>
          <p className="muted small">
            Die Rechnung bleibt mit Inhalt und Grund erhalten und behält ihre Nummer – eine Nummer
            wird nie zurückgenommen. Verrechnete Rapportzeilen werden wieder frei und lassen sich
            neu verrechnen. Aus der normalen Übersicht verschwindet der Beleg; unter dem Filter
            «Storniert» bleibt er auffindbar.
          </p>

          {stornoOffen ? (
            <div className="stack">
              <label>
                Grund der Stornierung
                <input
                  autoComplete="off"
                  id="storno-grund"
                  value={stornoGrund}
                  onChange={(e) => setStornoGrund(e.target.value)}
                  placeholder="z.B. Kunde hat den Auftrag zurückgezogen"
                />
              </label>
              <ActionButton
                id="btn-storno-ok"
                variant="secondary"
                actionClass="B"
                disabled={stornoGrund.trim().length === 0}
                labels={{ idle: 'Stornieren', busy: 'Storniert …', success: 'Storniert' }}
                onAction={async () => {
                  const r = await api.cancelDocument(id, stornoGrund.trim(), uuidv7())
                  notify.info(r.message)
                  setStornoOffen(false)
                  await laden()
                }}
                onError={(err) => notifyError(err)}
              />
            </div>
          ) : (
            <ActionButton
              id="btn-storno-start"
              variant="quiet"
              labels={{ idle: 'Stornieren …', busy: '…' }}
              onAction={async () => setStornoOffen(true)}
            />
          )}
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------

/**
 * Eine Position ändern.
 *
 * Der Preis wird in Franken eingegeben und in Rappen verschickt. Umgerechnet
 * wird genau hier, an der Kante zwischen Eingabefeld und Netz – überall sonst
 * im Programm sind Beträge ganze Rappen, damit nie gerundet werden muss.
 *
 * Gelesen wird mit `pruefePosition` (ui/Positionswerte.ts): «1'250.50» ist
 * CHF 1'250.50, und was keine Zahl ist, bleibt am Feld stehen und geht nicht
 * als 0.00 hinaus.
 *
 * `basisVersion` ist die Version, die diese Ansicht zuletzt geladen hat. Hat
 * jemand den Beleg inzwischen woanders geändert, lehnt der Server ab, die
 * Ansicht lädt neu – und die Eingabe bleibt im Feld stehen, bis sie geprüft
 * und noch einmal übernommen ist.
 */
function ZeileBearbeiten({
  documentId,
  line,
  basisVersion,
  onFertig,
  onAbbruch,
  onKonflikt
}: {
  documentId: string
  line: DocumentLine
  basisVersion: number
  onFertig: () => Promise<void>
  onAbbruch: () => void
  onKonflikt: () => Promise<void>
}): JSX.Element {
  const [description, setDescription] = useState(line.description)
  const [quantity, setQuantity] = useState(String(Number(line.quantity)))
  const [unit, setUnit] = useState(line.unit)
  const [preis, setPreis] = useState((line.unitPriceRp / 100).toFixed(2))
  const [rabatt, setRabatt] = useState(String(Number(line.discountPercent)))
  /* Eine Katalogart wird nur mitgeschickt, wenn jemand die Katalogwerte übernimmt. */
  const [kind, setKind] = useState<'arbeit' | 'material' | null>(null)
  /* Ein gewählter Katalogeintrag, dessen Preis/Einheit noch nicht übernommen ist. */
  const [angebot, setAngebot] = useState<Katalogeintrag | null>(null)
  const katalog = useKatalogvorschlaege(description)
  const befund = pruefePosition({ menge: quantity, preis, rabatt })
  const fehler = befund.ok ? {} : befund.fehler
  const ohneText = description.trim().length === 0
  const speichernId = `f-speichern-${line.id}`

  function katalogwerte(c: Katalogeintrag): void {
    setUnit(c.unit)
    setPreis((c.unitPriceRp / 100).toFixed(2))
    setKind(c.kind)
    setAngebot(null)
  }

  return (
    <div className="stack editline">
      <label>
        Text
        <Vorschlagsfeld
          id={`f-text-${line.id}`}
          wert={description}
          aufWert={setDescription}
          vorschlaege={katalog.vorschlaege}
          mindestlaenge={2}
          placeholder="Bezeichnung oder Code aus dem Katalog"
          aufAuswahl={(v) => {
            /*
              Eine bestehende Position wird nicht ungefragt überschrieben: Die
              Bezeichnung kommt aus dem Katalog, weil sie gewählt wurde. Preis
              und Einheit nie von selbst – auch 0.00 kann gewollt sein
              (Garantie, Kulanz). Sie stehen als Angebot mit eigenem Knopf
              darunter. Nur eine leere Einheit wird gefüllt.
            */
            const c = katalog.eintrag(v.schluessel)
            if (!c) return
            if (unit.trim() === '') setUnit(c.unit)
            const jetzt = pruefePosition({ menge: '1', preis, rabatt: '' })
            const gleich =
              jetzt.ok && jetzt.werte.unitPriceRp === c.unitPriceRp && (unit.trim() || c.unit) === c.unit
            setAngebot(gleich ? null : c)
          }}
        />
      </label>
      {angebot && (
        <div className="rowactions" id={`f-katalog-${line.id}`}>
          <span className="muted small">
            Katalog: CHF {(angebot.unitPriceRp / 100).toFixed(2)} / {angebot.unit}
          </span>
          <button
            type="button"
            className="line-remove"
            id={`f-katalog-uebernehmen-${line.id}`}
            onClick={() => katalogwerte(angebot)}
          >
            Preis und Einheit übernehmen
          </button>
        </div>
      )}
      <Positionszahlen
        praefix={`f-${line.id}`}
        aufEnter={() => document.getElementById(speichernId)?.click()}
        quantity={quantity}
        unit={unit}
        preis={preis}
        rabatt={rabatt}
        fehler={fehler}
        ids={{
          menge: `f-menge-${line.id}`,
          einheit: `f-einheit-${line.id}`,
          preis: `f-preis-${line.id}`,
          rabatt: `f-rabatt-${line.id}`
        }}
        setQuantity={setQuantity}
        setUnit={setUnit}
        setPreis={setPreis}
        setRabatt={setRabatt}
      />
      <div className="rowactions">
        <ActionButton
          id={speichernId}
          actionClass="B"
          disabled={!befund.ok || ohneText}
          labels={{ idle: 'Übernehmen', busy: 'Speichert …', success: 'Gespeichert' }}
          onAction={async () => {
            if (!befund.ok || ohneText) return
            await api.updateDocumentLine(
              documentId,
              line.id,
              {
                description: description.trim(),
                quantity: befund.werte.quantity,
                unit: unit.trim(),
                unitPriceRp: befund.werte.unitPriceRp,
                discountPercent: befund.werte.discountPercent,
                ...(kind ? { kind } : {}),
                basisVersion
              },
              uuidv7()
            )
            await onFertig()
          }}
          onError={(err) => {
            if (istVersionskonflikt(err)) {
              notify.info(KONFLIKT_TEXT)
              void onKonflikt()
              return
            }
            notifyError(err)
          }}
        />
        <ActionButton
          variant="quiet"
          labels={{ idle: 'Abbrechen', busy: '…' }}
          onAction={async () => onAbbruch()}
        />
      </div>
    </div>
  )
}

const KONFLIKT_TEXT =
  'Dieser Beleg wurde inzwischen an anderer Stelle geändert. Die Ansicht ist neu geladen; ' +
  'deine Eingabe steht noch im Feld – bitte prüfen und noch einmal übernehmen.'

function istVersionskonflikt(err: unknown): boolean {
  return err instanceof ApiError && err.options.code === 'VERSION_CONFLICT'
}

/**
 * Menge, Einheit, Preis und Rabatt – für Ändern und Hinzufügen dieselben Felder.
 *
 * Die Fehler stehen direkt unter dem Feld, mit `aria-invalid` und
 * `aria-describedby`, so wie in der Express-Erfassung (belegfehler.css).
 */
function Positionszahlen({
  praefix,
  aufEnter,
  quantity,
  unit,
  preis,
  rabatt,
  fehler,
  ids,
  setQuantity,
  setUnit,
  setPreis,
  setRabatt
}: {
  praefix: string
  /* Enter in einem Zahlenfeld speichert – derselbe Knopf, dieselben Prüfungen. */
  aufEnter?: () => void
  quantity: string
  unit: string
  preis: string
  rabatt: string | null
  fehler: Partial<Record<Positionsfeld, string>>
  ids: { menge: string; einheit: string; preis: string; rabatt?: string }
  setQuantity: (v: string) => void
  setUnit: (v: string) => void
  setPreis: (v: string) => void
  setRabatt?: (v: string) => void
}): JSX.Element {
  const fehlerId = (feld: Positionsfeld): string => `${praefix}-${feld}-fehler`
  const enter = (e: { key: string; preventDefault: () => void }): void => {
    if (e.key === 'Enter' && aufEnter) {
      e.preventDefault()
      aufEnter()
    }
  }
  const meldung = (feld: Positionsfeld): JSX.Element | null =>
    fehler[feld] ? (
      <span className="o-feldfehler" id={fehlerId(feld)} role="alert">
        {fehler[feld]}
      </span>
    ) : null

  return (
    <>
      <div className="tri">
        <label>
          Menge
          <input
            autoComplete="off"
            id={ids.menge}
            inputMode="decimal"
            value={quantity}
            aria-invalid={fehler.menge ? true : undefined}
            aria-describedby={fehler.menge ? fehlerId('menge') : undefined}
            onChange={(e) => setQuantity(e.target.value)}
            onKeyDown={enter}
          />
          {meldung('menge')}
        </label>
        <label>
          Einheit
          <input
            autoComplete="off"
            id={ids.einheit}
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            onKeyDown={enter}
          />
        </label>
        <label>
          Preis CHF
          <input
            autoComplete="off"
            id={ids.preis}
            inputMode="decimal"
            value={preis}
            aria-invalid={fehler.preis ? true : undefined}
            aria-describedby={fehler.preis ? fehlerId('preis') : undefined}
            onChange={(e) => setPreis(e.target.value)}
            onKeyDown={enter}
          />
          {meldung('preis')}
        </label>
      </div>
      {rabatt !== null && setRabatt && ids.rabatt && (
        <label>
          Rabatt %
          <input
            autoComplete="off"
            id={ids.rabatt}
            inputMode="decimal"
            value={rabatt}
            aria-invalid={fehler.rabatt ? true : undefined}
            aria-describedby={fehler.rabatt ? fehlerId('rabatt') : undefined}
            onChange={(e) => setRabatt(e.target.value)}
            onKeyDown={enter}
          />
          {meldung('rabatt')}
        </label>
      )}
    </>
  )
}

/** Eine Position ohne Rapportbezug – Anfahrt, Kleinmaterial, Pauschale. */
function FreiePosition({
  documentId,
  basisVersion,
  onFertig,
  onKonflikt
}: {
  documentId: string
  basisVersion: number
  onFertig: () => Promise<void>
  onKonflikt: () => Promise<void>
}): JSX.Element {
  const [auf, setAuf] = useState(false)
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [unit, setUnit] = useState('Pausch.')
  const [preis, setPreis] = useState('0.00')
  const [kind, setKind] = useState<'arbeit' | 'material'>('arbeit')
  const katalog = useKatalogvorschlaege(description)
  /* Hier gibt es kein Rabattfeld – eine neue Position beginnt ohne Rabatt. */
  const befund = pruefePosition({ menge: quantity, preis, rabatt: '' })
  const fehler = befund.ok ? {} : befund.fehler

  /* Nach dem Hinzufügen bleibt das Formular offen – die nächste Position folgt meist sofort. */
  function leeren(): void {
    setDescription('')
    setQuantity('1')
    setUnit('Pausch.')
    setPreis('0.00')
    setKind('arbeit')
  }

  if (!auf) {
    return (
      <ActionButton
        id="btn-freie-position"
        variant="secondary"
        labels={{ idle: 'Position hinzufügen', busy: '…' }}
        onAction={async () => setAuf(true)}
      />
    )
  }

  return (
    <div className="stack editline">
      <label>
        Text
        <Vorschlagsfeld
          id="neu-text"
          wert={description}
          aufWert={setDescription}
          vorschlaege={katalog.vorschlaege}
          mindestlaenge={2}
          placeholder="Bezeichnung oder Code – z.B. Anfahrt"
          aufAuswahl={(v) => {
            /* Eine neue Position: alles aus dem Katalog, alles bleibt änderbar. */
            const c = katalog.eintrag(v.schluessel)
            if (!c) return
            setUnit(c.unit)
            setPreis((c.unitPriceRp / 100).toFixed(2))
            setKind(c.kind)
            if (quantity.trim() === '') setQuantity('1')
          }}
        />
      </label>
      <Positionszahlen
        praefix="neu"
        aufEnter={() => document.getElementById('btn-neu-speichern')?.click()}
        quantity={quantity}
        unit={unit}
        preis={preis}
        rabatt={null}
        fehler={fehler}
        ids={{ menge: 'neu-menge', einheit: 'neu-einheit', preis: 'neu-preis' }}
        setQuantity={setQuantity}
        setUnit={setUnit}
        setPreis={setPreis}
      />
      <div className="rowactions">
        <ActionButton
          id="btn-neu-speichern"
          actionClass="B"
          disabled={description.trim().length === 0 || !befund.ok}
          labels={{ idle: 'Hinzufügen', busy: 'Speichert …', success: 'Hinzugefügt' }}
          onAction={async () => {
            if (!befund.ok) return
            await api.addDocumentLine(
              documentId,
              {
                description: description.trim(),
                quantity: befund.werte.quantity,
                unit: unit.trim(),
                unitPriceRp: befund.werte.unitPriceRp,
                vatRate: 8.1,
                kind,
                basisVersion
              },
              uuidv7()
            )
            leeren()
            await onFertig()
            document.getElementById('neu-text')?.focus()
          }}
          onError={(err) => {
            if (istVersionskonflikt(err)) {
              notify.info(KONFLIKT_TEXT)
              void onKonflikt()
              return
            }
            notifyError(err)
          }}
        />
        <ActionButton
          variant="quiet"
          labels={{ idle: 'Abbrechen', busy: '…' }}
          onAction={async () => setAuf(false)}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

/** Der Server legt den letzten Fehlgrund am Beleg ab – falls vorhanden, zeigen. */
function lastError(beleg: DocumentDetail): string | null {
  return beleg.lastError && beleg.lastError.trim() ? beleg.lastError : null
}

/**
 * Versand.
 *
 * Drei Dinge unterscheiden diesen Bildschirm von einem gewöhnlichen
 * Mailformular:
 *
 * 1. Die Empfängeradresse kommt aus dem Kundendatensatz, ist aber änderbar.
 *    Rechnungen gehen oft an die Buchhaltung und nicht an den Bauleiter, und
 *    wer dafür erst den Kunden bearbeiten muss, schickt sie an die falsche
 *    Adresse.
 *
 * 2. «unklar» ist ein eigener Zustand mit eigenem Text. Wer nach einem
 *    Verbindungsabbruch «fehlgeschlagen» liest, drückt nochmal – und der Kunde
 *    hat zwei Rechnungen.
 *
 * 3. Die Historie steht unter dem Formular und nicht hinter einem Reiter. Wer
 *    gleich nochmal senden will, soll vorher sehen, was schon rausgegangen ist.
 */
function Versand({
  documentId,
  beleg,
  onGesendet
}: {
  documentId: string
  beleg: DocumentDetail
  onGesendet: () => Promise<void>
}): JSX.Element {
  const [wege, setWege] = useState<Versandwege | null>(null)
  const [frage, setFrage] = useState(false)
  /* Der Vermerk hat eine Geldfolge – deshalb eine Rückfrage davor. */
  const [vermerkFrage, setVermerkFrage] = useState(false)
  /*
    Der E-Mail-Editor.

    `null` heisst zu. Offen trägt er den Weg, den Empfänger und die beiden
    Felder – und zwar schon gefüllt: Der Server liefert die Vorlage mit
    ersetzten Platzhaltern, damit im Feld eine fertige Nachricht steht und
    keine geschweiften Klammern.
  */
  const [mail, setMail] = useState<{
    weg: 'benutzer' | 'firma' | 'kunde'
    empfaenger: string
    betreff: string
    text: string
  } | null>(null)

  const laden = useCallback(async (): Promise<void> => {
    setWege(await api.versandwege(documentId))
  }, [documentId])

  useEffect(() => {
    void laden().catch(() => setWege(null))
  }, [laden])

  /**
   * Den Editor öffnen – nichts geht ohne ihn hinaus.
   *
   * Vorher löste ein Klick auf «An Kunden senden» unmittelbar den Versand
   * aus. Niemand sah, was beim Kunden ankam; der Text war im Serverquelltext
   * zusammengesetzt und als Ganzes unerreichbar. Jetzt geht erst der Editor
   * auf, und erst der Knopf darin sendet.
   */
  async function editorOeffnen(weg: 'benutzer' | 'firma' | 'kunde'): Promise<void> {
    const v = await api.mailvorschlag(documentId, weg)
    setFrage(false)
    setMail({ weg, empfaenger: v.empfaenger, betreff: v.betreff, text: v.text })
  }

  async function senden(): Promise<void> {
    if (!mail) return
    const r = await api.versenden(documentId, mail.weg, uuidv7(), {
      betreff: mail.betreff,
      text: mail.text
    })
    notify.ok(
      mail.weg === 'kunde'
        ? `${beleg.type === 'offerte' ? 'Offerte' : 'Rechnung'} an ${r.empfaenger} gesendet.`
        : `PDF an ${r.empfaenger} gesendet. Von dort kannst du es weiterleiten.`
    )
    /*
      Der Entwurf bleibt stehen, bis der Versand wirklich durch ist.

      Scheitert er, wirft `api.versenden` – dann läuft diese Zeile nicht, der
      Editor bleibt offen und der geänderte Text ist noch da. Wer eine Mail
      umformuliert hat und sie wegen eines Mailserverfehlers neu tippen muss,
      verliert zweimal: einmal den Versand und einmal die Arbeit.
    */
    setMail(null)
    await laden()
    await onGesendet()
  }

  async function vermerken(): Promise<void> {
    await api.alsVersendetVermerken(documentId, uuidv7())
    notify.ok('Als versendet vermerkt.')
    setVermerkFrage(false)
    await laden()
    await onGesendet()
  }

  /**
   * Einen unklaren Versand klären.
   *
   * Zwei Wege, und beide enden mit einem geklärten Zustand: «angekommen»
   * vermerkt den Beleg als versendet, «nicht angekommen» schliesst den
   * Versuch und gibt ihn wieder frei. Gesendet wird in beiden Fällen nichts.
   */
  async function klaeren(ergebnis: 'angekommen' | 'nicht-angekommen'): Promise<void> {
    await api.versandKlaeren(documentId, ergebnis, uuidv7())
    notify.ok(
      ergebnis === 'angekommen'
        ? 'Als versendet vermerkt. Der Beleg bleibt gesperrt – jetzt aus dem richtigen Grund.'
        : 'Versuch als gescheitert geschlossen. Der Beleg ist wieder bearbeitbar.'
    )
    await laden()
    await onGesendet()
  }

  async function vermerkWeg(): Promise<void> {
    await api.vermerkEntfernen(documentId, uuidv7())
    notify.ok('Vermerk entfernt.')
    await laden()
    await onGesendet()
  }

  if (!wege) {
    return (
      <div className="card">
        <h2>Versand</h2>
        <p className="muted">Wird geladen …</p>
      </div>
    )
  }

  const kv = wege.kundenversand
  return (
    <div className="card">
      <h2>Versand</h2>

      {/*
        Der Weg über die eigene Adresse steht zuerst, weil ihn jeder Betrieb
        hat. Der direkte Kundenversand verlangt einen eigenen Mailserver, und
        den richtet niemand am ersten Tag ein.
      */}
      <p className="muted small">
        Wir senden dir das PDF an deine eigene Adresse. Du leitest es danach selbst weiter.
      </p>

      <div className="rowactions">
        {wege.adressenGleich ? (
          <ActionButton
            id="btn-versand-selbst"
            variant="secondary"
            actionClass="B"
            labels={{ idle: 'An eigene Adresse senden', busy: 'Öffnet …', success: 'Bereit' }}
            onAction={async () => editorOeffnen('benutzer')}
            onError={(err) => notifyError(err)}
          />
        ) : (
          <button
            type="button"
            className="action-btn action-secondary"
            id="btn-versand-selbst"
            onClick={() => setFrage(true)}
          >
            An eigene Adresse senden
          </button>
        )}

        {/*
          Der Knopf ist immer da – auch wenn er nicht geht. Ein fehlender Knopf
          wirft die Frage auf, ob es die Möglichkeit gibt; ein gesperrter mit
          Begründung beantwortet sie.
        */}
        <ActionButton
          id="btn-versand-kunde"
          actionClass="B"
          disabled={!kv.moeglich}
          labels={{ idle: 'An Kunden senden', busy: 'Öffnet …', success: 'Bereit' }}
          onAction={async () => editorOeffnen('kunde')}
          onError={(err) => notifyError(err)}
        />
      </div>

      {!kv.moeglich && kv.grund && (
        <p className="muted small" id="versand-kunde-grund">
          {kv.grund}
        </p>
      )}

      {/*
        Der vierte Weg: ausgedruckt und in die Post.

        Er steht abgesetzt unter den beiden Mailknöpfen, weil er etwas anderes
        ist – hier sendet Raporto nichts, hier bestätigt ein Mensch. Ohne diesen
        Weg bliebe die Rechnung eines Betriebs, der auf Papier verschickt, für
        immer «nicht versendet» und würde nie überfällig.
      */}
      {wege.vermerk && !wege.vermerk.am && (
        <div id="vermerk-block" style={{ marginTop: 14 }}>
          <p className="muted small" style={{ marginBottom: 6 }}>
            Ausgedruckt und per Post verschickt oder persönlich übergeben?
          </p>
          <button
            type="button"
            className="action-btn action-secondary"
            id="btn-als-versendet"
            disabled={!wege.vermerk.moeglich}
            onClick={() => setVermerkFrage(true)}
          >
            Als versendet vermerken
          </button>
          {!wege.vermerk.moeglich && wege.vermerk.grund && (
            <p className="muted small" id="vermerk-grund">
              {wege.vermerk.grund}
            </p>
          )}
        </div>
      )}

      {wege.vermerk?.am && (
        <div id="vermerk-gesetzt" className="rowactions" style={{ marginTop: 14 }}>
          <span className="pill small">Als versendet vermerkt</span>
          <span className="muted small">
            {new Date(wege.vermerk.am).toLocaleString('de-CH')}
            {wege.vermerk.von ? ` · ${wege.vermerk.von}` : ''} · nicht per E-Mail
          </span>
          <ActionButton
            id="btn-vermerk-weg"
            variant="secondary"
            actionClass="B"
            labels={{ idle: 'Vermerk entfernen', busy: 'Entfernt …', success: 'Entfernt' }}
            onAction={vermerkWeg}
            onError={(err) => notifyError(err)}
          />
        </div>
      )}

      {/*
        Der unklare Versand – der einzige Zustand, der von selbst nicht weggeht.

        Er sperrt den Beleg, ohne ihn als versendet auszuweisen: Die Mail kann
        beim Kunden liegen. Wer hier nichts anbietet, hinterlässt einen Beleg,
        der weder fertig noch bearbeitbar ist. Deshalb stehen beide Auswege
        direkt da, mit dem Satz, der sagt, was zu prüfen ist.

        Bewusst KEIN Knopf «Nochmals senden» an dieser Stelle. Erst klären,
        dann senden – in dieser Reihenfolge, und als zwei Entscheidungen.
      */}
      {beleg.status === 'versand_unklar' && (
        <div className="o-fehlerband" id="versand-unklar-band" style={{ marginTop: 14 }}>
          <strong>Der letzte Versand ist unklar geblieben.</strong>
          <p className="small" style={{ margin: '6px 0 0' }}>
            Der Mailserver hat nicht eindeutig geantwortet. Die Mail kann beim Kunden liegen –
            darum bleibt dieser Beleg bis zur Klärung gesperrt. Sieh in deinem Postausgang oder
            beim Kunden nach und sag dann, was gilt.
          </p>
          <div className="rowactions" style={{ marginTop: 10 }}>
            <ActionButton
              id="btn-unklar-angekommen"
              variant="secondary"
              actionClass="B"
              labels={{
                idle: 'Sie ist angekommen',
                busy: 'Wird vermerkt …',
                success: 'Vermerkt'
              }}
              onAction={async () => klaeren('angekommen')}
              onError={(err) => notifyError(err)}
            />
            <ActionButton
              id="btn-unklar-nicht-angekommen"
              variant="secondary"
              actionClass="B"
              labels={{
                idle: 'Sie ist nicht angekommen',
                busy: 'Wird freigegeben …',
                success: 'Freigegeben'
              }}
              onAction={async () => klaeren('nicht-angekommen')}
              onError={(err) => notifyError(err)}
            />
          </div>
        </div>
      )}

      {vermerkFrage && (
        <div className="o-dialog-grund" id="vermerk-rueckfrage">
          <div className="o-dialog">
            <h3>Als versendet vermerken?</h3>
            <p className="muted small">
              Damit gilt diese {beleg.type === 'offerte' ? 'Offerte' : 'Rechnung'} als beim Kunden.
              Ab der Zahlungsfrist kann sie überfällig werden und in der Mahnliste erscheinen.
            </p>
            <p className="muted small">
              <strong>Raporto verschickt dabei nichts.</strong> Der Vermerk hält nur fest, was du
              selbst getan hast.
            </p>
            <div className="rowactions">
              <button
                type="button"
                className="action-btn action-secondary"
                id="btn-vermerk-abbrechen"
                onClick={() => setVermerkFrage(false)}
              >
                Abbrechen
              </button>
              <ActionButton
                id="btn-vermerk-bestaetigen"
                actionClass="B"
                labels={{ idle: 'Vermerken', busy: 'Speichert …', success: 'Vermerkt' }}
                onAction={vermerken}
                onError={(err) => notifyError(err)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Was schon hinausgegangen ist – vor dem zweiten Klick sichtbar. */}
      {(wege.gesendet.anBenutzer || wege.gesendet.anFirma || wege.gesendet.anKunde) && (
        <dl className="counts" id="versand-verlauf">
          {wege.gesendet.anBenutzer && (
            <>
              <dt>An deine Adresse</dt>
              <dd>{new Date(wege.gesendet.anBenutzer).toLocaleString('de-CH')}</dd>
            </>
          )}
          {wege.gesendet.anFirma && (
            <>
              <dt>An die Firmenadresse</dt>
              <dd>{new Date(wege.gesendet.anFirma).toLocaleString('de-CH')}</dd>
            </>
          )}
          {wege.gesendet.anKunde && (
            <>
              <dt>An den Kunden</dt>
              <dd id="versand-an-kunde">
                {new Date(wege.gesendet.anKunde).toLocaleString('de-CH')} · {wege.kundenMail}
              </dd>
            </>
          )}
        </dl>
      )}

      {frage && (
        <div className="o-dialog-grund" id="versand-auswahl">
          <div className="o-dialog">
            <h3>An welche Adresse?</h3>
            <p className="muted small">
              Deine persönliche Adresse und die Adresse des Betriebs sind nicht dieselbe.
            </p>
            <div className="rowactions">
              <ActionButton
                id="btn-versand-person"
                actionClass="B"
                labels={{
                  idle: `An ${wege.benutzerMail}`,
                  busy: 'Sendet …',
                  success: 'Gesendet'
                }}
                onAction={async () => editorOeffnen('benutzer')}
                onError={(err) => notifyError(err)}
              />
              <ActionButton
                id="btn-versand-firma"
                variant="secondary"
                actionClass="B"
                labels={{
                  idle: `An ${wege.firmaMail || 'die Firmenadresse'}`,
                  busy: 'Sendet …',
                  success: 'Gesendet'
                }}
                disabled={!wege.firmaMail}
                onAction={async () => editorOeffnen('firma')}
                onError={(err) => notifyError(err)}
              />
              <ActionButton
                variant="quiet"
                labels={{ idle: 'Abbrechen', busy: '…' }}
                onAction={async () => setFrage(false)}
              />
            </div>
          </div>
        </div>
      )}

      {mail && (
        <MailEditor
          entwurf={mail}
          titel={
            mail.weg === 'kunde'
              ? `${beleg.type === 'offerte' ? 'Offerte' : 'Rechnung'} an den Kunden senden`
              : 'PDF an die eigene Adresse senden'
          }
          anhang={`${beleg.number ?? 'Beleg'}.pdf`}
          aufAendern={setMail}
          aufSenden={senden}
          aufAbbrechen={() => setMail(null)}
        />
      )}

    </div>
  )
}

// ---------------------------------------------------------------------------

const ZAHLUNG_TEXT: Record<Zahlungsstand['status'], string> = {
  offen: 'offen',
  teilbezahlt: 'teilbezahlt',
  bezahlt: 'bezahlt',
  ueberbezahlt: 'überbezahlt',
  storniert: 'storniert'
}

/**
 * Zahlungen.
 *
 * Der Zahlungsstand steht bewusst NEBEN dem Belegzustand und nicht darin. Eine
 * gesendete Rechnung kann offen sein, eine nie gesendete bezahlt – wer beides
 * in eine Zeile presst, muss sich für eine der beiden Wahrheiten entscheiden
 * und verliert die andere.
 *
 * Erfasst wird in Franken, gespeichert in Rappen. Die Umrechnung passiert genau
 * hier, an der Kante zwischen Eingabefeld und Netz.
 */
function Zahlungen({
  documentId,
  beleg,
  rolle
}: {
  documentId: string
  beleg: DocumentDetail
  rolle: 'superuser' | 'worker'
}): JSX.Element {
  const [daten, setDaten] = useState<({ payments: Payment[] } & Zahlungsstand) | null>(null)
  const [offen, setOffen] = useState(false)
  const [betrag, setBetrag] = useState('')
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10))
  const [notiz, setNotiz] = useState('')
  const [storniert, setStorniert] = useState<string | null>(null)
  const [grund, setGrund] = useState('')

  const laden = useCallback(async (): Promise<void> => {
    const r = await api.listPayments(documentId)
    setDaten(r)
    // Der Vorschlag ist der offene Betrag – in den allermeisten Fällen zahlt
    // der Kunde genau das, und Abtippen ist die häufigste Fehlerquelle.
    setBetrag((r.openRp / 100).toFixed(2))
  }, [documentId])

  useEffect(() => {
    void laden().catch(() => setDaten(null))
  }, [laden])

  if (!daten)
    return (
      <div className="card">
        <p className="muted">Zahlungen werden geladen …</p>
      </div>
    )

  const gueltige = daten.payments.filter((p) => !p.voidedAt)

  return (
    <div className="card">
      <div className="listhead">
        <h2>Zahlung</h2>
        <span
          className={`pill ${daten.status === 'bezahlt' ? 'superuser' : daten.status === 'ueberbezahlt' ? 'warn' : ''}`}
          id="zahlungsstatus"
        >
          {ZAHLUNG_TEXT[daten.status]}
        </span>
      </div>

      <dl className="counts">
        <dt>Rechnungsbetrag</dt>
        <dd>CHF {formatChf(daten.totalRp)}</dd>
        <dt>Bereits bezahlt</dt>
        <dd id="bezahlt">CHF {formatChf(daten.paidRp)}</dd>
        <dt>
          <strong>{daten.overpaidRp > 0 ? 'Zu viel bezahlt' : 'Noch offen'}</strong>
        </dt>
        <dd id="offen-betrag">
          <strong>CHF {formatChf(daten.overpaidRp > 0 ? daten.overpaidRp : daten.openRp)}</strong>
        </dd>
      </dl>

      {daten.overpaidRp > 0 && (
        <p className="small">
          Der Kunde hat mehr überwiesen als verrechnet. Das ist kein Fehler des Programms – aber
          jemand sollte es klären.
        </p>
      )}

      {gueltige.length > 0 || daten.payments.length > 0 ? (
        <ul className="records" id="zahlungsliste">
          {daten.payments.map((p) => (
            <li key={p.id}>
              <div className={`line-row ${p.voidedAt ? 'entwertet' : ''}`}>
                <span className="rec-name">
                  CHF {formatChf(p.amountRp)}
                  {p.voidedAt && <span className="pill small crit">storniert</span>}
                  {p.source === 'camt' && <span className="pill small">Bank</span>}
                </span>
                <span className="rec-sub">
                  {p.paidAt.slice(0, 10)}
                  {p.byName ? ` · ${p.byName}` : ''}
                  {p.note ? ` · ${p.note}` : ''}
                  {p.voidedAt ? ` · storniert von ${p.voidedByName ?? '—'}: ${p.voidReason}` : ''}
                </span>
                {rolle === 'superuser' && !p.voidedAt && (
                  <button
                    type="button"
                    className="line-remove"
                    id={`storno-${p.id}`}
                    onClick={() => {
                      setStorniert(p.id)
                      setGrund('')
                    }}
                  >
                    Stornieren
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted" id="keine-zahlung">
          Noch keine Zahlung erfasst.
        </p>
      )}

      {storniert && (
        <div className="stack editline">
          <p className="muted small">
            Die Zahlung bleibt sichtbar und zählt nicht mehr mit. Gelöscht wird sie nicht – eine
            verschwundene Zahlung wäre eine unerklärliche Änderung am offenen Betrag.
          </p>
          <label>
            Grund
            <input
              autoComplete="off"
              id="storno-grund-zahlung"
              value={grund}
              onChange={(e) => setGrund(e.target.value)}
              placeholder="z.B. Betrag vertippt"
            />
          </label>
          <div className="rowactions">
            <ActionButton
              id="btn-storno-zahlung"
              actionClass="B"
              disabled={grund.trim().length === 0}
              labels={{ idle: 'Stornieren', busy: 'Storniert …', success: 'Storniert' }}
              onAction={async () => {
                await api.voidPayment(storniert, grund.trim(), uuidv7())
                setStorniert(null)
                await laden()
                notify.info('Zahlung storniert.')
              }}
              onError={(err) => notifyError(err)}
            />
            <ActionButton
              variant="quiet"
              labels={{ idle: 'Abbrechen', busy: '…' }}
              onAction={async () => setStorniert(null)}
            />
          </div>
        </div>
      )}

      {rolle === 'superuser' &&
        beleg.status !== 'storniert' &&
        (offen ? (
          <div className="stack editline">
            <div className="tri">
              <label>
                Betrag CHF
                <input
                  autoComplete="off"
                  id="zahlung-betrag"
                  inputMode="decimal"
                  value={betrag}
                  onChange={(e) => setBetrag(e.target.value)}
                />
              </label>
              <label>
                Datum
                <Datumsfeld
                  id="zahlung-datum"
                  value={datum}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={setDatum}
                />
              </label>
              <label>
                Notiz
                <input
                  autoComplete="off"
                  id="zahlung-notiz"
                  value={notiz}
                  onChange={(e) => setNotiz(e.target.value)}
                  placeholder="optional"
                />
              </label>
            </div>
            <div className="rowactions">
              <ActionButton
                id="btn-zahlung-speichern"
                actionClass="B"
                disabled={Number(betrag.replace(',', '.')) <= 0}
                labels={{ idle: 'Zahlung erfassen', busy: 'Speichert …', success: 'Erfasst' }}
                onAction={async () => {
                  const rappen = Math.round(Number(betrag.replace(',', '.')) * 100)
                  const r = await api.recordPayment(
                    documentId,
                    { amountRp: rappen, paidAt: datum, note: notiz.trim() || undefined },
                    uuidv7()
                  )
                  setOffen(false)
                  setNotiz('')
                  await laden()
                  notify.ok(
                    r.status === 'bezahlt'
                      ? 'Rechnung vollständig bezahlt.'
                      : r.status === 'ueberbezahlt'
                        ? `Überbezahlt um CHF ${formatChf(r.overpaidRp)}.`
                        : `Noch offen: CHF ${formatChf(r.openRp)}.`
                  )
                }}
                onError={(err) => notifyError(err)}
              />
              <ActionButton
                variant="quiet"
                labels={{ idle: 'Abbrechen', busy: '…' }}
                onAction={async () => setOffen(false)}
              />
            </div>
          </div>
        ) : (
          <ActionButton
            id="btn-zahlung-start"
            variant={daten.status === 'offen' ? 'primary' : 'secondary'}
            labels={{
              idle: daten.status === 'offen' ? 'Zahlung erfassen' : 'Weitere Zahlung erfassen',
              busy: '…'
            }}
            onAction={async () => setOffen(true)}
          />
        ))}
    </div>
  )
}
